import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem("afl_cart");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("afl_cart", JSON.stringify(cartItems));
    } catch (e) {
      console.error("Cart save error", e);
    }
  }, [cartItems]);

  const addToCart = (product, quantity = 1, selectedColor = null, selectedDesign = null) => {
    const color = selectedColor || (product.available_colors && product.available_colors[0]) || "Standard Set Pack";
    const design = selectedDesign || (product.designs && product.designs[0]) || "Default Design";
    const cartItemId = `${product.id}-${color}-${design}`;

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.cartItemId === cartItemId);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        updated[existingIndex].item_total = updated[existingIndex].quantity * updated[existingIndex].price;
        toast.success(`Updated set quantity in Cart: ${updated[existingIndex].quantity} Sets`);
        return updated;
      } else {
        const newItem = {
          cartItemId,
          product_id: product.id,
          product_name: product.name,
          category: product.category,
          price: product.retail_price,
          wholesale_price: product.wholesale_price,
          quantity,
          selected_color: color,
          selected_design: design,
          is_complete_set: product.is_complete_set ?? true,
          set_details: "Complete Set",
          image: (product.images && product.images[0]) || "https://images.unsplash.com/photo-1652722464455-ec026ef74703?crop=entropy&cs=srgb&fm=jpg&q=85",
          weight_kg: Number(product.weight_kg) || 0.5,
          item_total: product.retail_price * quantity,
        };
        toast.success(`Added Complete Set to Cart! (${color})`);
        return [...prev, newItem];
      }
    });
  };

  const updateQuantity = (cartItemId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: newQty, item_total: item.price * newQty }
          : item
      )
    );
  };

  const removeFromCart = (cartItemId) => {
    setCartItems((prev) => prev.filter((item) => item.cartItemId !== cartItemId));
    toast.info("Item removed from Cart");
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem("afl_cart");
  };

  const totalItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const subtotal = cartItems.reduce((acc, item) => acc + item.item_total, 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        totalItemsCount,
        subtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
